import { AnimatePresence, motion } from "motion/react";
import { useContext, useEffect } from "react";

import styled from "styled-components/macro";

import classNames from "classnames";

import FnrErrorBoundary from "UI/FnrErrorBoundary";
import { Z_MODAL } from "UI/zIndex";

import Portal from "Utilities/PortalMaker";

import "./FnrModal_body_styles.scss";
import useFnrModalState, {
  FnrModalProvider,
  FnrModalState,
} from "./fnrModalState";

const childAnimations = {
  variants: {
    initial: (transitionType) =>
      transitionType === "peekHandle"
        ? { x: window.innerWidth, opacity: 1, scale: 1, y: 0 }
        : { scale: 0.5, opacity: 0, y: "50%" },
    open: (transitionType) => ({
      scale: 1,
      opacity: 1,
      y: 0,
      x: 0,
      transition:
        transitionType === "peekHandle"
          ? { type: "tween", duration: 0.45, ease: [0.4, 0, 0.2, 1] }
          : undefined,
    }),
    closed: (transitionType) =>
      transitionType === "peekHandle"
        ? {
            x: window.innerWidth,
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
              type: "tween",
              duration: 0.45,
              ease: [0.4, 0, 0.2, 1],
            },
          }
        : {
            scale: 0.5,
            opacity: 0,
            y: "50%",
            transition: { type: "tween", duration: 0.3, ease: "backIn" },
          },
    receded: {
      scale: 0.98,
      opacity: 1,
      y: 0,
      transition: { type: "tween", duration: 0.3, ease: "easeOut" },
    },
  },
  initial: "initial",
  animate: "open",
  exit: "closed",
};

const backdropVariants = {
  hidden: (transitionType) => ({
    opacity: 0,
    transition:
      transitionType === "peekHandle"
        ? { duration: 0.3, delay: 0.25 }
        : undefined,
  }),
  visible: { opacity: 1 },
};

export default function FnrModalWithState({
  children,
  show,
  setShow,
  positionTopIfMobile,
  onWillClose,
  onFinishedClosing,
  onBackgroundClick = () => {},
  showInvisibly,
  scaleDown,
  custom,
  ...props
}) {
  return (
    <FnrModalProvider
      show={show}
      showInvisibly={showInvisibly}
      setShow={setShow}
      positionTopIfMobile={positionTopIfMobile}
      onWillClose={onWillClose}
      onFinishedClosing={onFinishedClosing}
    >
      <FnrModal
        onBackgroundClick={onBackgroundClick}
        scaleDown={scaleDown}
        custom={custom}
        {...props}
      >
        {children}
      </FnrModal>
    </FnrModalProvider>
  );
}

function FnrModal({
  children,
  onBackgroundClick = () => {},
  scaleDown,
  custom,
  ...props
}) {
  const {
    show,
    animationComplete,
    positionTopIfMobile,
    isTouchOnly,
    showInvisibly,
  } = useFnrModalState();

  useEffect(() => {
    if (show) document.body.classList.add("FnrModal_showing");
    else document.body.classList.remove("FnrModal_showing");

    return () => document.body.classList.remove("FnrModal_showing");
  }, [show]);

  return (
    <AnimatePresence custom={custom}>
      {show ? (
        <Portal to={document.getElementById("FnrOverlayHolder")}>
          <Backdrop
            className={classNames({
              positionTopIfMobile: positionTopIfMobile && isTouchOnly,
            })}
            variants={backdropVariants}
            custom={custom}
            initial="hidden"
            animate={showInvisibly ? "hidden" : "visible"}
            exit="hidden"
            onClick={onBackgroundClick}
            id="fnrmodalbackground"
            data-cy="fnrmodalbackground"
          >
            <ModalWrapper
              {...childAnimations}
              custom={custom}
              animate={scaleDown ? "receded" : "open"}
              onAnimationComplete={animationComplete}
              {...props}
            >
              <FnrErrorBoundary boundaryName="FnrModal">
                {children}
              </FnrErrorBoundary>
            </ModalWrapper>
          </Backdrop>
        </Portal>
      ) : null}
    </AnimatePresence>
  );
}

export function useCloseModal() {
  const context = useContext(FnrModalState);

  if (!context) {
    throw new Error(
      "useCloseModal must be used inside a FlocknoteAppStateProvider"
    );
  }
  if (!context.setShow) {
    throw new Error(
      "setShow must be passed into FnrModal for useCloseModal to work"
    );
  }

  return context.close;
}

const ModalWrapper = styled(motion.div)`
  position: relative; // Containing block for absolutely-positioned children (e.g. close button)
  max-height: 100%; // This is necessary for proper vertical scrolling on small screens
`;

const Backdrop = styled(motion.div)`
  position: fixed;
  z-index: ${Z_MODAL};
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: rgba(0, 0, 0, 0.5);

  display: flex;
  justify-content: center;
  align-items: center;

  &.positionTopIfMobile {
    align-items: flex-start;
    & ${ModalWrapper} {
      margin-top: 40px;
    }
  }
`;

export const ModalContent = styled.div`
  position: relative;
  background: #eceff0;
  max-width: calc(100vw - 60px);
  box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  box-sizing: border-box;
  padding: 20px;

  /* CHROME (and anywhere backdrop-filter is supported) */
  @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
    background: rgba(236, 239, 240, 0.8);
    backdrop-filter: blur(12px) saturate(2);
    -webkit-backdrop-filter: blur(12px) saturate(2);
  }
`;
